<template>
  <div class="video-gen">
    <!-- ============ 卡片一：生成设置 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span class="hd-title">🎬 AI 视频生成（MiniMax H3）</span>
          <div class="hd-right">
            <el-tag size="small" type="warning" effect="plain">文生 / 图生 / 真人参考 · 4-15 秒直出</el-tag>
            <a href="https://design.minimaxi.com/h3" target="_blank" class="hd-link">🖥️ 官方演示与示例</a>
            <img src="/img/h3-videogen.png" alt="MiniMax H3" class="hd-h3-logo" />
          </div>
        </div>
      </template>

      <!-- 与官网一致的四步工作流引导 -->
      <el-steps :active="stepActive" align-center class="h3-steps" finish-status="success">
        <el-step title="① 选创作模式" description="文生 / 图生(首尾帧) / 真人参考" />
        <el-step title="② 加参考素材" description="图片·视频·音频（按模式）" />
        <el-step title="③ 写描述 / 设输出" description="描述 + 时长/画幅/清晰度" />
        <el-step title="④ 生成出片" description="约 1-3 分钟，自动入素材库" />
      </el-steps>

      <el-form label-width="110px" label-position="right">
        <el-form-item label="生成模式">
          <el-radio-group v-model="form.mode">
            <el-radio-button value="t2v">📝 文生视频</el-radio-button>
            <el-radio-button value="i2v">🖼️ 图生视频</el-radio-button>
            <el-radio-button value="ref2v">🧑 真人参考</el-radio-button>
          </el-radio-group>
          <div class="mode-hint">
            {{ modeHint }}
          </div>
        </el-form-item>

        <!-- 图生：首帧/尾帧 -->
        <template v-if="form.mode === 'i2v'">
          <el-form-item label="首帧图片" required>
            <div class="frame-pick">
              <div class="frame-upload">
                <el-upload :auto-upload="false" :show-file-list="false" accept="image/*" :on-change="f => uploadFrameAsset('first', f)" style="display:inline-block">
                  <el-button size="small" type="primary" plain>📤 上传本机图片</el-button>
                </el-upload>
                <span class="frame-tip">首帧 = 视频第一幕画面，必传</span>
              </div>
              <el-select v-model="form.first_frame_asset_id" filterable clearable placeholder="或选择素材库已有图片" style="width:100%">
                <el-option v-for="a in imageAssets" :key="a.id" :label="a.title || a.file_name" :value="a.id">
                  <div class="asset-opt">
                    <img v-if="a.url" :src="a.url" class="asset-thumb" />
                    <span>{{ a.title || a.file_name }}</span>
                  </div>
                </el-option>
              </el-select>
            </div>
          </el-form-item>
          <el-form-item label="尾帧图片">
            <div class="frame-pick">
              <div class="frame-upload">
                <el-upload :auto-upload="false" :show-file-list="false" accept="image/*" :on-change="f => uploadFrameAsset('last', f)" style="display:inline-block">
                  <el-button size="small" type="primary" plain>📤 上传本机图片</el-button>
                </el-upload>
                <span class="frame-tip">可选，尾帧 = 视频最后一幕画面，用于首尾衔接</span>
              </div>
              <el-select v-model="form.last_frame_asset_id" filterable clearable placeholder="或选择素材库已有图片" style="width:100%">
                <el-option v-for="a in imageAssets" :key="a.id" :label="a.title || a.file_name" :value="a.id">
                  <div class="asset-opt">
                    <img v-if="a.url" :src="a.url" class="asset-thumb" />
                    <span>{{ a.title || a.file_name }}</span>
                  </div>
                </el-option>
              </el-select>
            </div>
          </el-form-item>
        </template>

        <!-- 真人参考 -->
        <template v-if="form.mode === 'ref2v'">
          <el-form-item label="参考图片">
            <div class="frame-upload">
              <el-upload :auto-upload="false" :show-file-list="false" accept="image/*" :on-change="f => uploadRefAsset('image', f)" style="display:inline-block">
                <el-button size="small" type="primary" plain>📤 上传本机参考图</el-button>
              </el-upload>
              <span class="frame-tip">人物形象照 / 商品图 / 场景图均可；多张按上传顺序自动编号（参考图1/2…）</span>
            </div>
            <el-select v-model="form.reference_image_ids" multiple collapse-tags filterable placeholder="或选素材库参考图（≤9 张）" style="width:100%">
              <el-option v-for="a in imageAssets" :key="a.id" :label="a.title || a.file_name" :value="a.id">
                <div class="asset-opt">
                  <img v-if="a.url" :src="a.url" class="asset-thumb" />
                  <span>{{ a.title || a.file_name }}</span>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="参考视频">
            <div class="frame-upload">
              <el-upload :auto-upload="false" :show-file-list="false" accept="video/*" :on-change="f => uploadRefAsset('video', f)" style="display:inline-block">
                <el-button size="small" type="warning" plain>📤 上传本机参考视频</el-button>
              </el-upload>
              <span class="frame-tip">提供动作/运镜/节奏参考；官方要求单段 ≤15s，超长请先用剪辑工具裁剪</span>
            </div>
            <el-select v-model="form.reference_video_ids" multiple collapse-tags filterable placeholder="或选素材库参考视频（≤3 个）" style="width:100%">
              <el-option v-for="a in videoAssets" :key="a.id" :label="a.title || a.file_name" :value="a.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="参考音频">
            <div class="frame-upload">
              <el-upload :auto-upload="false" :show-file-list="false" accept="audio/*" :on-change="f => uploadRefAsset('audio', f)" style="display:inline-block">
                <el-button size="small" type="success" plain>📤 上传本机参考音频</el-button>
              </el-upload>
              <span class="frame-tip">提供音色/对白/氛围（音色克隆）</span>
            </div>
            <el-select v-model="form.reference_audio_ids" multiple collapse-tags filterable placeholder="或选素材库参考音频（≤3 个）" style="width:100%">
              <el-option v-for="a in audioAssets" :key="a.id" :label="a.title || a.file_name" :value="a.id" />
            </el-select>
          </el-form-item>
          <!-- 多参考上限 + 编号引用引导（真人参考模式） -->
          <div class="ref-guide">
            <div class="rg-title">🧭 全能参考使用指引（多图/视频/音频混合）</div>
            <ul>
              <li><b>上限</b>：参考图 ≤9 张 · 参考视频 ≤3 段 · 参考音频 ≤3 段，<b>合计 ≤12 个文件</b>（超出会被拦截）</li>
              <li><b>编号引用</b>：素材按下拉顺序自动编号 —— 第一张图＝“参考图1”、第二张＝“参考图2”…视频/音频同理。在下方描述里<b>按编号写明每份素材的用途</b>，模型才会精准对应（如“把参考图1 的人放到参考图2 的场景里，动作参考参考视频1”）</li>
              <li><b>推荐分工</b>：图片定「人物 / 商品 / 场景」长相与外观 · 视频借「动作 / 运镜 / 节奏」 · 音频给「音色 / 对白 / 氛围」</li>
            </ul>
          </div>
          <div v-if="form.reference_image_ids.length || form.reference_video_ids.length || form.reference_audio_ids.length" class="ref-order">
            <span class="ro-label">📎 已选素材（描述中请用下列编号指代）：</span>
            <el-tag v-for="(id, i) in form.reference_image_ids" :key="'i' + id" size="small" type="primary" effect="plain">参考图{{ i + 1 }} #{{ id }}</el-tag>
            <el-tag v-for="(id, i) in form.reference_video_ids" :key="'v' + id" size="small" type="warning" effect="plain">参考视频{{ i + 1 }} #{{ id }}</el-tag>
            <el-tag v-for="(id, i) in form.reference_audio_ids" :key="'a' + id" size="small" type="success" effect="plain">参考音频{{ i + 1 }} #{{ id }}</el-tag>
          </div>
        </template>

        <el-form-item label="视频描述" required>
          <el-input
            v-model="form.prompt"
            type="textarea"
            :rows="7"
            maxlength="1000"
            show-word-limit
            placeholder="描述画面内容、镜头运动、风格等。例：一杯咖啡放在木质桌面，热气袅袅升起，镜头缓缓推进展示拉花细节，午后阳光斜洒，氛围温馨，商业广告风格，画面清晰"
          />
          <div class="mode-hint" v-if="form.mode === 'ref2v'">✍️ 真人参考写法：在描述里按编号指定用途，例——“把<b>参考图1</b>中的人物（脸与着装）放进<b>参考图2</b>的场景里，手中拿着<b>参考图3</b>的商品对镜头介绍，动作与口型参考<b>参考视频1</b>，节奏自然真实”。</div>
          <div class="prompt-help">
            <div class="ph-title">
              <span>💡 写描述词技巧（文生视频 / 图生视频通用）</span>
              <span class="ph-btns">
                <el-button size="small" text type="primary" @click="fillExample">📝 填入示例</el-button>
                <el-button size="small" text type="success" :loading="aiWriting" @click="askAiWrite">🤖 AI 代写描述（直出回填）</el-button>
              </span>
            </div>
            <div class="ph-body">
              描述越具体，成片越接近预期。推荐结构：<b>① 主体（什么产品/场景）② 动作（在做什么）③ 镜头（推进/环绕/俯拍）④ 环境与风格（灯光/色调/广告感）⑤ 声音（可选）</b>。
              图生视频只需描述「让画面发生什么变化」；文生视频则要写清主体+场景。
            </div>
            <div class="ph-adv">
              ✨ <b>H3 进阶技巧</b>：
              ① <b>[运镜] 指令</b>——关键句后加 <code class="mv">[推进]</code> <code class="mv">[环绕]</code> <code class="mv">[拉升]</code> <code class="mv">[俯拍]</code>，镜头更听话；
              ② <b>写声音</b>——H3 原生立体声（对白/音效/环境音同一条出片），描述里写明「舒缓背景音乐」「有鸟鸣声」会真的生成对应声音；
              ③ <b>多镜头一条出</b>——想要多组画面，用「SHOT 1 …… / CUT TO ……」分段，一次生成自动剪辑
            </div>
            <div class="ph-example">
              示例：「暖阳午后，一只橘猫在窗台伸懒腰 [推进]，镜头缓缓环绕一圈 [环绕]，光线柔和暖调，画面干净治愈，有轻声鸟鸣，治愈系短视频风格」
            </div>
          </div>
        </el-form-item>

        <el-form-item label="时长">
          <div class="slider-row">
            <el-slider v-model="form.duration" :min="4" :max="15" :step="1" show-stops style="flex:1" />
            <span class="slider-val">{{ form.duration }} 秒</span>
          </div>
        </el-form-item>

        <el-form-item label="清晰度">
          <el-radio-group v-model="form.resolution">
            <el-radio-button value="768P">768P（1.2点/秒）</el-radio-button>
            <el-radio-button value="2K">2K（1.73点/秒）</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="画幅">
          <el-radio-group v-model="form.ratio">
            <el-radio-button value="9:16">9:16 竖屏</el-radio-button>
            <el-radio-button value="16:9">16:9 横屏</el-radio-button>
            <el-radio-button value="21:9">21:9 电影</el-radio-button>
            <el-radio-button value="1:1">1:1 方形</el-radio-button>
            <el-radio-button value="4:3">4:3</el-radio-button>
            <el-radio-button value="3:4">3:4</el-radio-button>
          </el-radio-group>
          <div class="mode-hint" v-if="form.mode === 't2v'">官方规则：文生视频画幅必填（不支持自适应）；图生/真人参考画幅自动跟随参考图，此处可不选</div>
          <div class="mode-hint" v-else>画幅自动跟随参考图（官方 adaptive），此处选择不生效</div>
        </el-form-item>

        <el-divider content-position="left">发布设置</el-divider>

        <el-form-item label="生成后入队">
          <el-switch v-model="form.create_publish" />
          <span class="mode-hint" style="margin-left:8px">完成后自动加入发布队列（半自动：剪贴板+跳转App）</span>
        </el-form-item>
        <el-form-item v-if="form.create_publish" label="视频标题">
          <el-input v-model="form.publish_title" placeholder="抖音视频标题（≤30字）" maxlength="30" show-word-limit />
        </el-form-item>
        <el-form-item v-if="form.create_publish" label="发布文案">
          <el-input v-model="form.publish_desc" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="作品文案/口播稿（可留空，用视频描述生成）" />
        </el-form-item>
        <el-form-item v-if="form.create_publish" label="话题标签">
          <el-input v-model="form.publish_tags" placeholder="逗号分隔，如：产品宣传,商业广告,实拍" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" size="large" :loading="submitting" @click="submit">
            🚀 生成视频（约 {{ (form.resolution === '2K' ? Math.round(form.duration * 1.73) : Math.round(form.duration * 1.2)) }} 找客点）
          </el-button>
          <el-button size="large" @click="loadAssets">刷新素材库</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- ============ 卡片二：任务列表 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>📋 视频任务</span>
          <el-button size="small" text type="primary" @click="refreshAll">刷新状态</el-button>
        </div>
      </template>

      <el-empty v-if="!tasks.length" description="还没有视频任务，先在上面生成一条吧" />

      <div v-for="t in tasks" :key="t.task_id" class="task-item">
        <div class="task-head">
          <span class="task-mode">{{ modeName(t.mode) }}</span>
          <span class="task-prompt" :title="t.prompt">{{ t.prompt || '(无描述)' }}</span>
          <el-tag :type="statusTag(t.status)" size="small">{{ statusText(t.status) }}</el-tag>
          <el-tag v-if="t.status === 'done' && t.queue_id" size="small" type="warning" style="margin-left:2px">📤 已入发布队列</el-tag>
          <span class="task-cost" v-if="t.duration">≈ {{ (t.resolution === '2K' ? Math.round(t.duration * 1.73) : Math.round(t.duration * 1.2)) }} 找客点</span>
          <el-button size="small" text type="danger" @click="delTask(t)" title="从任务列表移除（视频文件可在素材库删除）">✕</el-button>
        </div>
        <div class="task-body">
          <template v-if="t.status === 'done' && t.video_url">
            <video :src="t.video_url" controls style="max-width:320px;max-height:180px;border-radius:8px" />
            <div class="task-meta" v-if="t.duration || t.resolution">
              {{ t.duration }}s · {{ t.resolution }} · {{ t.ratio }}
              <el-button size="small" type="primary" text @click="downloadVideo(t)">⬇ 保存视频</el-button>
              <el-button size="small" type="success" @click="goPublish(t)">📤 去发布队列</el-button>
            </div>
            <div class="task-save-tip">手机端建议：点「保存视频」→ 系统分享面板选「存储到照片/相册」或直接发抖音；也可长按视频画面保存</div>
            <div class="task-save-tip" v-if="t.status === 'done' && !t.queue_id">已完成并保存到素材库，但未加入发布队列（生成时关了"生成后入队"或历史任务）。可在素材库找到该视频后点「🚀 上传」发布</div>
          </template>
          <template v-else-if="t.status === 'failed'">
            <el-alert type="error" :closable="false" :title="t.error || '生成失败'" />
          </template>
          <template v-else>
            <el-progress :percentage="t.status === 'running' ? 60 : 30" :indeterminate="t.status === 'running'" :duration="2" style="max-width:320px" />
          </template>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'VideoGen',
  data() {
    return {
      form: {
        account_id: 0,
        mode: 't2v',
        prompt: '',
        first_frame_asset_id: 0,
        last_frame_asset_id: 0,
        reference_image_ids: [],
        reference_video_ids: [],
        reference_audio_ids: [],
        duration: 10,
        resolution: '768P',
        ratio: '9:16',
        create_publish: true,
        publish_title: '',
        publish_desc: '',
        publish_tags: ''
      },
      aiWriting: false,
      imageAssets: [],
      videoAssets: [],
      audioAssets: [],
      submitting: false,
      tasks: [],
      _timer: null
    }
  },
  computed: {
    // 与官网一致的创作模式说明（text-to-video / image-to-video / Ref2VA 全能参考）
    modeHint() {
      return {
        t2v: '文生视频（官方 text-to-video）：只写文字描述，从零生成画面 + 原生声音。适合产品宣传/场景/口播，描述越具体越好，可加 [推进]/[环绕] 等运镜词',
        i2v: '图生视频（官方 image-to-video 首帧/首尾帧）：上传产品图/效果图作为首帧（可加尾帧），让指定画面自然动起来。只需描述「画面发生了什么变化」，画幅自动跟随图片',
        ref2v: '真人参考（官方 Ref2VA 全能参考）：传形象照(≤9张) + 动作视频(≤3段) + 声音(≤3段)，克隆真人形象/动作/音色生成——数字人口播、带货、讲解都行'
      }[this.form.mode]
    },
    // 四步引导进度：选模式→素材齐→写好描述，每完成一层前进一步
    stepActive() {
      let a = this.form.mode ? 1 : 0
      let hasAssets = false
      if (this.form.mode === 't2v') hasAssets = true
      else if (this.form.mode === 'i2v') hasAssets = this.form.first_frame_asset_id > 0
      else hasAssets = this.form.reference_image_ids.length > 0 || this.form.reference_video_ids.length > 0
      if (hasAssets) a += 1
      if (this.form.prompt && this.form.prompt.trim()) a += 1
      return a
    }
  },
  created() {
    const acc = localStorage.getItem('current_account_id')
    if (acc) this.form.account_id = Number(acc)
    const uid = localStorage.getItem('user_id')
    if (uid) this.form.user_id = Number(uid)
    this.loadAssets()
    this.loadTasks()
    this._timer = setInterval(this.refreshAll, 8000)
  },
  beforeUnmount() {
    if (this._timer) clearInterval(this._timer)
  },
  methods: {
    modeName(m) {
      return { t2v: '📝 文生', i2v: '🖼️ 图生', ref2v: '🧑 真人' }[m] || m
    },
    statusText(s) {
      return { pending: '排队中', running: '生成中…', done: '完成 ✅', failed: '失败 ❌' }[s] || s
    },
    statusTag(s) {
      return { pending: 'info', running: 'warning', done: 'success', failed: 'danger' }[s] || 'info'
    },
    fileUrl(path) {
      if (!path) return ''
      const name = String(path).split(/[\\/]/).pop()
      return '/media/' + encodeURIComponent(name)
    },
    async loadAssets() {
      try {
        const r = await api.get('/assets', { params: { limit: 100 } })
        const list = (r.assets || []).map(a => ({ ...a, file_name: String(a.file_path).split(/[\\/]/).pop(), url: this.fileUrl(a.file_path) }))
        this.imageAssets = list.filter(a => a.type === 'image')
        this.videoAssets = list.filter(a => a.type === 'video')
        this.audioAssets = list.filter(a => a.type === 'audio')
      } catch (e) { console.error(e) }
    },
    loadTasks() {
      try {
        this.tasks = JSON.parse(localStorage.getItem('video_tasks') || '[]')
      } catch (e) { this.tasks = [] }
    },
    saveTasks() {
      localStorage.setItem('video_tasks', JSON.stringify(this.tasks))
    },
    async submit() {
      if (!this.form.user_id) {
        this.$confirm('生成 AI 视频需要登录并充值找客点，现在去登录？', '未登录', {
          confirmButtonText: '去登录', cancelButtonText: '取消', type: 'warning'
        }).then(() => this.$router.push('/login')).catch(() => {})
        return
      }
      if (!this.form.prompt.trim()) {
        this.$message.warning('请填写视频描述')
        return
      }
      if (this.form.mode === 'i2v' && !this.form.first_frame_asset_id) {
        this.$message.warning('图生视频请选择首帧图片')
        return
      }
      if (this.form.mode === 'ref2v' && !this.form.reference_image_ids.length && !this.form.reference_video_ids.length) {
        this.$message.warning('真人参考请至少选择一张参考图或一个参考视频')
        return
      }
      this.submitting = true
      try {
        const payload = {
          account_id: this.form.account_id,
          mode: this.form.mode,
          prompt: this.form.prompt.trim(),
          first_frame_asset_id: this.form.first_frame_asset_id,
          last_frame_asset_id: this.form.last_frame_asset_id,
          reference_image_ids: this.form.reference_image_ids,
          reference_video_ids: this.form.reference_video_ids,
          reference_audio_ids: this.form.reference_audio_ids,
          duration: this.form.duration,
          resolution: this.form.resolution,
          ratio: this.form.ratio,
          create_publish: this.form.create_publish,
          publish_title: this.form.publish_title,
          publish_desc: this.form.publish_desc,
          publish_tags: this.form.publish_tags
        }
        const r = await api.post('/content/video', payload)
        this.tasks.unshift({
          task_id: r.task_id,
          mode: this.form.mode,
          prompt: this.form.prompt.trim(),
          status: 'pending',
          duration: this.form.duration,
          resolution: this.form.resolution,
          ratio: this.form.ratio,
          error: '',
          video_url: ''
        })
        this.saveTasks()
        this.$message.success('视频任务已提交，生成中…（约 1-3 分钟），完成后自动加入发布队列')
      } catch (e) {
        this.$message.error(e.message || '提交失败')
      } finally {
        this.submitting = false
      }
    },
    async refreshAll() {
      const pending = this.tasks.filter(t => ['pending', 'running'].includes(t.status))
      if (!pending.length) return
      for (const t of pending) {
        try {
          const r = await api.get('/content/tasks/' + t.task_id)
          t.status = r.status
          if (r.status === 'running' || r.status === 'pending') continue
          if (r.status === 'done') {
            const paths = r.result_paths || []
            if (paths.length) t.video_url = this.fileUrl(paths[0])
            if (r.result) {
              t.duration = r.result.duration || t.duration
              t.resolution = r.result.resolution || t.resolution
              t.ratio = r.result.ratio || t.ratio
              t.queue_id = r.result.queue_id || null   // 发布队列定位
            }
            this.$message.success(t.queue_id ? '视频生成完成 ✅ 已自动加入发布队列' : '视频生成完成 ✅')
          } else if (r.status === 'failed') {
            t.error = r.error || '生成失败'
          }
        } catch (e) {
          // 忽略单任务查询失败，下轮再试
        }
      }
      this.saveTasks()
    },
    async downloadVideo(t) {
      // 手机端：优先系统分享（可「存储到照片/相册」或直接发抖音）；失败回退浏览器下载
      const url = t.video_url
      if (!url) return
      const isMobile = /Android|iPhone|iPad|HarmonyOS/i.test(navigator.userAgent)
      const fname = 'H3_' + ((t.prompt || '').trim().slice(0, 10) || 'AI视频') + '_' + (t.duration || 10) + 's.mp4'
      try {
        const resp = await fetch(url, { cache: 'no-store' })
        if (resp.ok) {
          const blob = await resp.blob()
          const file = new File([blob], fname, { type: blob.type || 'video/mp4' })
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: 'H3 AI 视频',
              text: '视频已生成，可存储到相册或直接发布'
            })
            this.$message.success('已调起系统分享，选「存储到照片」即可存入相册')
            return
          }
        }
      } catch (e) { /* 用户取消分享或失败 → 走浏览器下载兜底 */ }
      const a = document.createElement('a')
      a.href = url
      a.download = fname
      document.body.appendChild(a)
      a.click()
      a.remove()
      this.$message.success(isMobile
        ? '已开始下载：安卓请在浏览器「下载管理」查看，长按可存相册；iPhone 在视频上长按可直接存照片'
        : '已开始下载')
    },
    async goPublish(t) {
      // 优先用任务快照里的 queue_id；没有（历史任务/未刷新）则向服务端反查，保证总能定位
      let qid = t && t.queue_id
      if (!qid && t && t.status === 'done') {
        try {
          const r = await api.get('/content/tasks/' + t.task_id)
          qid = (r.result && r.result.queue_id) || null
          if (qid) t.queue_id = qid
        } catch (e) { /* 反查失败则按无 queue_id 处理 */ }
      }
      const focus = qid ? '?focus=' + qid : ''
      this.$router.push('/publish' + focus)
      if (t) {
        this.$nextTick(() => {
          this.$message.success(qid
            ? '已跳转发布队列并高亮该视频（黄色高亮行，标题/文件就是这条）'
            : (t.status === 'done'
              ? '生成时未开启「生成后入队」，视频只存在素材库。已跳转发布队列，请到「素材库（AI生成）」对这条视频点「🚀 上传」'
              : '视频还在生成中，完成后会自动加入发布队列'))
        })
      }
    },
    delTask(t) {
      // 仅从任务列表移除（localStorage）；视频文件保留在素材库可删
      const idx = this.tasks.findIndex(x => x.task_id === t.task_id)
      if (idx >= 0) {
        this.tasks.splice(idx, 1)
        this.saveTasks()
        this.$message.info('已从任务列表移除；视频文件在「素材库（AI生成）」中管理，可在那里删除')
      }
    },
    async uploadFrameAsset(which, file) {
      // 首帧/尾帧本地上传 → 素材库 → 自动选中
      if (!file || !file.raw) return
      const fd = new FormData()
      fd.append('file', file.raw)
      fd.append('account_id', localStorage.getItem('current_account_id') || 0)
      fd.append('title', which === 'first' ? '视频首帧' : '视频尾帧')
      try {
        const r = await api.post('/assets', fd, { timeout: 120000 })
        this.$message.success('图片已上传 ✓ 已自动设为' + (which === 'first' ? '首帧' : '尾帧'))
        await this.loadAssets()
        if (which === 'first') this.form.first_frame_asset_id = r.id
        else this.form.last_frame_asset_id = r.id
      } catch (e) {
        this.$message.error('上传失败：' + (e.message || '请重试'))
      }
    },
    async uploadRefAsset(kind, file) {
      // 真人参考：本地上传参考图/视频/音频 → 素材库 → 自动加入对应参考列表
      if (!file || !file.raw) return
      const kindName = { image: '参考图', video: '参考视频', audio: '参考音频' }[kind]
      const fd = new FormData()
      fd.append('file', file.raw)
      fd.append('account_id', localStorage.getItem('current_account_id') || 0)
      fd.append('title', kindName)
      try {
        const r = await api.post('/assets', fd, { timeout: 240000 })
        await this.loadAssets()
        if (kind === 'image') this.form.reference_image_ids.push(r.id)
        else if (kind === 'video') this.form.reference_video_ids.push(r.id)
        else this.form.reference_audio_ids.push(r.id)
        this.$message.success('✅ 已上传并加入' + kindName + '（自动编号，多张按顺序引用）')
      } catch (e) {
        this.$message.error('上传失败：' + (e.message || '请重试'))
      }
    },
    fillExample() {
      this.form.prompt = '一杯咖啡放在木质桌面，热气袅袅升起，镜头缓缓推进到拉花细节，午后阳光斜洒，氛围温馨，商业广告风格，画面清晰'
      this.$message.success('已填入示例描述，可按您的产品修改')
    },
    async askAiWrite() {
      // AI 代写视频描述：输入一句话主题 → 后端 DeepSeek 生成 → 直接回填描述框
      const topic = (this.form.prompt || '').trim()
      try {
        const { value } = await this.$prompt('一句话描述想生成的内容（例：展示我的XX产品，突出XX卖点；或 咖啡店日常氛围）', '🤖 AI 代写视频描述', {
          inputPlaceholder: topic || '例如：卖新鲜水果的老板娘，热情招呼顾客，展示水果新鲜多汁',
          confirmButtonText: '生成', cancelButtonText: '取消', inputValue: topic || ''
        })
        if (!value || !value.trim()) { this.$message.warning('请先输入主题'); return }
        this.aiWriting = true
        let refs = ''
        if (this.form.mode === 'ref2v' && (this.form.reference_image_ids.length || this.form.reference_video_ids.length)) {
          const imgMap = {}; const vidMap = {}
          ;[...this.imageAssets, ...this.videoAssets].forEach(a => { imgMap[a.id] = a.title || a.file_name; vidMap[a.id] = a.title || a.file_name })
          const parts = []
          this.form.reference_image_ids.forEach((id, i) => parts.push(`参考图${i + 1}="${imgMap[id] || ('素材#' + id)}"`))
          this.form.reference_video_ids.forEach((id, i) => parts.push(`参考视频${i + 1}="${vidMap[id] || ('素材#' + id)}"`))
          refs = parts.join('，')
        }
        const r = await api.post('/assistant/video-prompt', { topic: value.trim(), mode: this.form.mode, refs })
        if (r && r.prompt) {
          this.form.prompt = r.prompt
          this.$message.success('✅ 描述已代写并填入，可直接提交（可按需微调）')
        } else {
          this.$message.error('AI 未返回内容，请重试')
        }
      } catch (e) {
        if (e === 'cancel' || (e && e.message && e.message.includes('cancel'))) return
        this.$message.error((e.response && e.response.data && e.response.data.detail) || '代写失败，请稍后重试')
      } finally { this.aiWriting = false }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.mode-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.5;
}
/* 2026-09-02 官网对齐新增 */
.card-header { flex-wrap: wrap; }
.hd-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.hd-title { font-weight: 600; }
.hd-h3-logo { width: 96px; border-radius: 6px; display: block; height: auto; }
.hd-link { font-size: 12px; color: #409eff; text-decoration: none; }
.hd-link:hover { text-decoration: underline; }
.h3-steps { margin: 4px 0 22px; padding: 14px 8px; border: 1px solid rgba(148,163,184,.14); border-radius: 12px; background: rgba(255,255,255,.02); }
.h3-steps :deep(.el-step__title) { font-size: 13px; color: #cbd5e1; }
.h3-steps :deep(.el-step__title.is-process) { color: #7ab8ff; font-weight: 600; }
.h3-steps :deep(.el-step__title.is-success), .h3-steps :deep(.el-step__title.is-finish) { color: #94e0a8; }
.h3-steps :deep(.el-step__description) { font-size: 11px; color: #64748b; }
.h3-steps :deep(.el-step__description.is-process) { color: #94a3b8; }
.h3-steps :deep(.el-step__description.is-success), .h3-steps :deep(.el-step__description.is-finish) { color: #7fb892; }
.h3-steps :deep(.el-step__icon) { background: rgba(15,23,42,.6); border-color: rgba(148,163,184,.35); color: #94a3b8; }
.h3-steps :deep(.el-step__head.is-process .el-step__icon), .h3-steps :deep(.el-step__head.is-wait .el-step__icon) { border-color: #409eff; color: #409eff; }
.h3-steps :deep(.el-step__head.is-success .el-step__icon) { border-color: #67c23a; color: #67c23a; }
.h3-steps :deep(.el-step__line) { background: rgba(148,163,184,.2); }
.h3-steps :deep(.el-step__line-inner) { border-width: 1px; }
.h3-steps :deep(.el-step__head.is-process) { color: #409eff; }
.h3-steps :deep(.el-step__head.is-success) { color: #67c23a; }
.ph-adv { margin-top: 10px; font-size: 12.5px; color: #6b7280; background: linear-gradient(90deg, rgba(88,28,135,.06), rgba(30,27,75,.05)); border-left: 3px solid #7c5cff; border-radius: 6px; padding: 10px 12px; line-height: 1.9; }
.ph-adv code { background: #f0f0f5; border-radius: 4px; padding: 1px 6px; font-size: 12px; color: #7c3aed; }
.ph-adv code.mv { background: #7c3aed; color: #fff; padding: 1px 8px; border-radius: 6px; }
.frame-pick { width: 100%; display: flex; flex-direction: column; gap: 8px; }
.frame-upload { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.frame-tip { font-size: 12px; color: #8fa3c8; }
.slider-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}
.slider-val {
  min-width: 48px;
  text-align: right;
  color: #606266;
  font-weight: 600;
}
.asset-opt {
  display: flex;
  align-items: center;
  gap: 8px;
}
.asset-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}
.task-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}
.task-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.task-mode {
  font-weight: 600;
  color: #409eff;
}
.task-prompt {
  flex: 1;
  min-width: 120px;
  color: #606266;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-cost {
  color: #b88230;
  font-size: 12px;
}
.task-body {
  margin-top: 10px;
}
.task-meta {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.task-save-tip {
  margin-top: 4px;
  font-size: 11px;
  color: #8fa3c8;
  line-height: 1.5;
}
.mt-16 {
  margin-top: 16px;
}
/* 描述词帮助块 */
.prompt-help {
  width: 100%;
  margin-top: 8px;
  background: rgba(37, 99, 235, 0.06);
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--el-text-color-regular, #cbd5e1);
}
.ph-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;
  font-weight: 600;
  color: var(--el-text-color-primary, #e2e8f0);
}
.ph-btns { display: inline-flex; gap: 2px; }
.ph-example {
  margin-top: 4px;
  padding: 6px 10px;
  background: rgba(37, 99, 235, 0.08);
  border-radius: 6px;
  color: var(--el-text-color-secondary, #94a3b8);
}
/* ===== 手机端：文字左靠 + 输入框最大化（需求：描述输入太窄） ===== */
@media (max-width: 767px) {
  .video-gen :deep(.el-form-item) {
    display: block;
  }
  .video-gen :deep(.el-form-item__label) {
    width: 100% !important;
    text-align: left;
    float: none;
    line-height: 1.4;
    margin-bottom: 4px;
    font-size: 15px;
  }
  .video-gen :deep(.el-form-item__content) {
    margin-left: 0 !important;
    width: 100%;
  }
  .video-gen :deep(.el-textarea__inner) { min-height: 96px; }
  .video-gen :deep(.el-input), .video-gen :deep(.el-select), .video-gen :deep(.el-slider) { width: 100% !important; }
  .video-gen :deep(.el-radio-group) { display: flex; flex-wrap: wrap; row-gap: 6px; }
  .ph-btns .el-button { padding: 0 6px; font-size: 12px; height: 28px; }
}
.ref-guide { margin: 4px 0 10px; background: rgba(37, 70, 150, .14); border: 1px solid rgba(96, 165, 250, .32); border-left: 4px solid #3b82f6; border-radius: 8px; padding: 10px 14px; }
.ref-guide ul { margin: 4px 0 0; padding-left: 18px; color: #a5b8d4; font-size: 13px; line-height: 1.9; }
.ref-guide .rg-title { font-weight: 700; color: #8ec1ff; font-size: 13px; }
.ref-order { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 2px 0 12px; padding: 8px 10px; background: rgba(250, 204, 21, .07); border: 1px dashed rgba(250, 204, 21, .38); border-radius: 8px; }
.ref-order .ro-label { color: #e8c968; font-size: 12px; }
/* ---------- 全局引导词：小字统一（深色主题） ---------- */
.prompt-help,
.prompt-help .ph-title,
.prompt-help .ph-body,
.prompt-help .ph-tip,
.mode-hint,
.ref-guide,
.ref-guide ul li,
.ref-guide .rg-title,
.ref-order .ro-label,
.frame-tip,
.row-tip,
.submit-tip,
.pq-tip {
  font-size: 11.5px !important;
  line-height: 1.8 !important;
}
.prompt-help .ph-title { font-weight: 600; }
.mode-hint { color: #94a3b8 !important; }
.frame-upload { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; width: 100%; margin-bottom: 6px; }
.frame-tip { color: #8fa3bd; }
/* 深色主题：步骤条标题不折行，避免步骤3错位 */
.h3-steps :deep(.el-step__title) { white-space: nowrap; }
</style>
